import { SpinnerPane } from '@criteria-labs/react'
import { Editor, darkTheme, lightTheme } from '@criteria-labs/react-editor'
import { useTheme } from 'next-themes'
import { Document, useDocumentText, useDocumentType } from '../documents/Document'
import { FileDocument } from '../documents/FileDocument'
import { JSONDocument, useJSONDocumentData } from '../documents/JSONDocument'
import { MarkdownDocument } from '../documents/MarkdownDocument'
import { OpenAPIDocument } from '../documents/OpenAPIDocument'
import { SourceRange } from '../issues/Issue'
import { DocumentTypeIndicator } from './DocumentTypeIndicator'

import openAPI3_0Schema from './schemas/openapi3_0/schema.json'
import openAPI3_1Schema from './schemas/openapi3_1/schema.json'

export function DocumentTextEditorContent({
  document,
  textSelection,
  jsonSchemas
}: {
  document: Document
  textSelection?: SourceRange
  jsonSchemas?: {
    uri: string
    fileMatch?: string[] | undefined
    schema?: any
  }[]
}) {
  const text = useDocumentText(document)

  let { resolvedTheme } = useTheme()
  const editorTheme = resolvedTheme === 'dark' ? darkTheme : lightTheme

  if (text === undefined) {
    return <SpinnerPane />
  }

  const language =
    document instanceof JSONDocument ? document.format : document instanceof MarkdownDocument ? 'markdown' : undefined

  return (
    <div
      className="flex h-full w-full flex-col"
      style={{
        backgroundColor: editorTheme.colors['editor.background']
      }}
    >
      <div className="h-0 flex-grow">
        <Editor
          value={text ?? ''}
          language={language}
          path={document instanceof FileDocument ? document.path : undefined}
          selection={textSelection}
          editable={true}
          onChange={(value: string | undefined) => {
            document.updateText(value ?? '')
          }}
          jsonSchemas={jsonSchemas}
        />
      </div>
      <div
        className="flex justify-end p-1 text-xs"
        style={{
          color: editorTheme.colors['editorCursor.foreground'],
          backgroundColor: editorTheme.colors['editor.background']
        }}
      >
        {document instanceof JSONDocument && <DocumentTypeIndicator document={document} />}
      </div>
    </div>
  )
}

function OpenAPIDocumentTextEditor({ document, ...props }: { document: OpenAPIDocument; textSelection?: SourceRange }) {
  const data = useJSONDocumentData(document)
  const openAPIVersion =
    typeof data === 'object' && data !== null && 'openapi' in data && typeof data.openapi === 'string'
      ? data.openapi
      : null

  let jsonSchemas = []
  if (openAPIVersion && openAPIVersion.match(/3\.1\.\d+/)) {
    jsonSchemas = [{ uri: openAPI3_1Schema.$id, fileMatch: [document.path], schema: openAPI3_1Schema }]
  } else if (openAPIVersion && openAPIVersion.match(/3\.0\.(0|1|2|3)/)) {
    jsonSchemas = [{ uri: openAPI3_0Schema.id, fileMatch: [document.path], schema: openAPI3_0Schema }]
  } else {
    // default to 3.1.0
    jsonSchemas = [{ uri: openAPI3_1Schema.$id, fileMatch: [document.path], schema: openAPI3_1Schema }]
  }

  return <DocumentTextEditorContent document={document} {...props} jsonSchemas={jsonSchemas} />
}

export function DocumentTextEditor({
  document,
  ...props
}: {
  document: Document
  preferredLocation: string
  setPreferredLocation: (location: string) => void
  textSelection?: SourceRange
}) {
  const documentType = useDocumentType(document)
  if (documentType === 'OpenAPIDocument') {
    return <OpenAPIDocumentTextEditor document={document as OpenAPIDocument} {...props} />
  } else {
    return <DocumentTextEditorContent document={document} {...props} />
  }
}
